import {OutlinedInput, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React from "react";
import type {SurveyVariable} from "../../shared/surveyVariables";

export interface QuestionEditorProps {
    question: SurveyQuestion
    patch: (partial: Partial<SurveyQuestion>) => void
    language: string
    /** The survey type's variables, for a type editor's own rich text (the Legal question's terms). */
    variables: SurveyVariable[]
    disabled?: boolean
    scaleLabelsVisible?: boolean
}

export const localizedValue = (text: LocalizedText | undefined, language: string) => text?.[language] ?? '';

export const patchLocalized = (
    {question, patch, language}: QuestionEditorProps,
    field: keyof SurveyQuestion,
    value: string
) => patch({
    [field]: {...(question[field] as LocalizedText | undefined), [language]: value},
} as Partial<SurveyQuestion>);

const EMOJI_FONTS = ['"Apple Color Emoji"', '"Segoe UI Emoji"', '"Noto Color Emoji"'];

interface InlineInputProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    inputRef?: React.Ref<HTMLInputElement>
    endAdornment?: React.ReactNode
}

/**
 * A plain text on the canvas (an option, a label, a placeholder): MUI's small outlined input, the frame the rich
 * text fields above it copy (Simon, 2026-10-07: outlined, never grey-filled). Emoji fonts, as labels carry emoji.
 */
export function InlineInput({value, onChange, placeholder, disabled, inputRef, endAdornment}: InlineInputProps) {
    return (
        <OutlinedInput
            size={'small'}
            fullWidth
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            inputRef={inputRef}
            endAdornment={endAdornment}
            sx={theme => ({
                '& .MuiOutlinedInput-input': {
                    fontFamily: [theme.typography.fontFamily, ...EMOJI_FONTS].join(', '),
                },
            })}
        />
    );
}

interface InlineLabelledFieldProps {
    label: string
    children: React.ReactNode
}

export function InlineLabelledField({label, children}: InlineLabelledFieldProps) {
    return (
        <Stack gap={0.5} sx={{flex: 1, minWidth: 0}}>
            <Typography variant={'body2'} color={'text.secondary'}>
                {label}
            </Typography>
            {children}
        </Stack>
    );
}
